import axios from 'axios';
import { getAuth } from 'firebase/auth';
import {
  get,
  getDatabase,
  orderByChild,
  query,
  ref,
  limitToFirst,
  onValue,
  push,
  update,
  set,
  limitToLast,
  child,
} from 'firebase/database';

import {
  getStorage,
  uploadBytes,
  ref as storageRef,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import i18next from 'i18next';
import { parse } from 'json2csv';

function handleFirebaseError(error, debug = false) {
  const errorCode = error?.code || 'unknown';
  const errorMessage = error?.message || 'Unexpected error';
  return debug ? [errorMessage, errorCode] : errorMessage;
}

const MAX_PLATFORM_ATTACHMENTS = 50;
const PRIORITY_NAME = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
};

const KANBAN_MODULE_TO_NAME = {
  seo: 'SEO',
  'paid-media': 'Mídia Paga',
};

export class Kanban {
  constructor(kanbanName, accountId) {
    if (!kanbanName) throw new Error('Kanban module name is required');
    this.kanbanName = kanbanName;
    this.accountId = accountId;
  }

  #getColumnsRef(columnId = null) {
    return ref(
      getDatabase(),
      `${this.kanbanName}/${this.accountId}/columns${columnId ? `/${columnId}` : ''}`,
    );
  }

  #getItemsRef(columnId = null, itemId = null) {
    return ref(
      getDatabase(),
      `${this.kanbanName}/${this.accountId}/items${columnId ? `/${columnId}` : ''}${itemId ? `/${itemId}` : ''}`,
    );
  }

  #getTypesRef(typeName = null, typeItemId = null) {
    return ref(
      getDatabase(),
      `${this.kanbanName}/${this.accountId}/types${typeName ? `/${typeName}` : ''}${typeItemId ? `/${typeItemId}` : ''}`,
    );
  }

  async getFirstColumn() {
    try {
      const columnsRef = this.#getColumnsRef();
      const columnsQuery = query(columnsRef, orderByChild('position'), limitToFirst(1));
      const snapshot = await get(columnsQuery);
      const columnsObj = snapshot.val() || {};
      const firstColumn = Object.values(columnsObj)[0] || null;
      if (!firstColumn) throw new Error('No columns found');
      return firstColumn;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async #addLogToItem(columnId, itemId, logData) {
    try {
      const itemRef = this.#getItemsRef(columnId, itemId);
      const itemSnapshot = await get(itemRef);
      const itemData = itemSnapshot.val();

      if (!itemData) throw new Error('Item not found');

      const updatedLogs = [
        ...(itemData.logs || []),
        { ...logData, createdAt: new Date().valueOf() },
      ];
      await update(itemRef, { logs: updatedLogs });
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async getColumns() {
    try {
      const columnsRef = this.#getColumnsRef();
      const snapshot = await get(columnsRef);
      const columnsObj = snapshot.val() || {};
      return Object.values(columnsObj);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async getColumn(columnId) {
    try {
      const columnRef = this.#getColumnsRef(columnId);
      const snapshot = await get(columnRef);
      return snapshot.val() || null;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async createColumn(columnData) {
    try {
      const columnsRef = this.#getColumnsRef();
      const columnKey = push(columnsRef);
      const newColumnRef = this.#getColumnsRef(columnKey.key);

      let newPosition = 0;
      const columns = await this.getColumns();
      if (columns.length > 0) {
        newPosition = Math.max(...columns.map((col) => col.position)) + 1;
      }

      const column = {
        ...columnData,
        id: columnKey.key,
        position: newPosition,
        createdAt: new Date().valueOf(),
      };

      await set(newColumnRef, column);
      return column;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async createMultipleColumns(columnsArr) {
    try {
      const columnsRef = this.#getColumnsRef();

      const columnsData = {};

      columnsArr.forEach((column, index) => {
        const newColumnRef = push(columnsRef);
        columnsData[newColumnRef.key] = {
          ...column,
          id: newColumnRef.key,
          position: index,
          createdAt: new Date().valueOf(),
        };
      });

      await set(columnsRef, columnsData);

      return true;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async updateColumn(columnId, columnData) {
    try {
      const columnRef = this.#getColumnsRef(columnId);

      const existingSnapshot = await get(columnRef);
      if (!existingSnapshot.exists()) {
        throw new Error('Column not found');
      }

      await update(columnRef, columnData);

      return { ...columnData, id: columnId };
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async deleteColumn(columnId, transferColumnId = null) {
    try {
      const columnRef = this.#getColumnsRef(columnId);
      const itemsRef = this.#getItemsRef(columnId);
      const columnSnapshot = await get(columnRef);
      if (!columnSnapshot.exists()) {
        throw new Error('Column not found');
      }

      if (transferColumnId) await this.transferItems(columnId, transferColumnId);

      await set(columnRef, null);
      await set(itemsRef, null);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  listenColumns(callback) {
    try {
      const columnsRef = this.#getColumnsRef();
      const columnsQuery = query(columnsRef, orderByChild('position'));
      const unsubscribe = onValue(columnsQuery, (snapshot) => {
        const data = snapshot.val();
        if (callback) callback(data);
      });
      return () => unsubscribe();
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async moveColumnForward(columnId) {
    try {
      const columns = await this.getColumns();
      const columnIndex = columns
        .sort((a, b) => a.position - b.position)
        .findIndex((col) => col.id === columnId);
      if (columnIndex === -1 || columnIndex === columns.length - 1) {
        throw new Error('Column not found or already at the end');
      }

      const currentColumn = columns[columnIndex];
      const nextColumn = columns[columnIndex + 1];
      const updates = {};
      updates[`${currentColumn.id}/position`] = nextColumn.position;
      updates[`${nextColumn.id}/position`] = currentColumn.position;

      const columnsRef = this.#getColumnsRef();
      await update(columnsRef, updates);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async moveColumnBackward(columnId) {
    try {
      const columns = await this.getColumns();
      const columnIndex = columns
        .sort((a, b) => a.position - b.position)
        .findIndex((col) => col.id === columnId);
      if (columnIndex <= 0) {
        throw new Error('Column not found or already at the beginning');
      }

      const currentColumn = columns[columnIndex];
      const prevColumn = columns[columnIndex - 1];
      const updates = {};
      updates[`${currentColumn.id}/position`] = prevColumn.position;
      updates[`${prevColumn.id}/position`] = currentColumn.position;

      const columnsRef = this.#getColumnsRef();
      await update(columnsRef, updates);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  listenToItems(columnId, callback, limit) {
    try {
      const columnRef = this.#getItemsRef(columnId);
      let columnQuery = query(columnRef, orderByChild('position'));
      if (limit) columnQuery = query(columnRef, orderByChild('position'), limitToFirst(limit));
      const unsubscribe = onValue(columnQuery, (snapshot) => {
        const data = snapshot.val();
        if (callback) callback(data);
      });

      return () => unsubscribe();
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async createItem(columnId, cardData, fixedPosition = 0) {
    try {
      if (!columnId) {
        const firstColumn = await this.getFirstColumn();
        columnId = firstColumn.id;
      }

      const columnRef = this.#getColumnsRef(columnId);
      const columnSnapshot = await get(columnRef);
      const columnData = columnSnapshot.val();
      const itemsRef = this.#getItemsRef(columnId);
      const itemKey = push(itemsRef);
      const newItemRef = this.#getItemsRef(columnId, itemKey.key);

      let position = fixedPosition;
      if (!fixedPosition) {
        const itemsQuery = query(itemsRef, orderByChild('position'), limitToLast(1));
        const snapshot = await get(itemsQuery);
        position = snapshot.exists() ? Object.values(snapshot.val())[0].position : 0;
      }

      await set(newItemRef, {
        ...cardData,
        id: itemKey.key,
        position,
        createdAt: new Date().valueOf(),
        columnId,
      });

      await update(columnRef, {
        length: (columnData.length || 0) + 1,
      });

      const user = getAuth().currentUser;
      await this.#addLogToItem(columnId, itemKey.key, { type: 'ITEM_CREATED', user: user?.uid });
      await this.notifyActivities(columnId, itemKey.key, 'ITEM_CREATED', user?.uid);

      return itemKey.key;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async listenItem(columnId, itemId, callback) {
    try {
      if (!columnId) {
        const columns = await this.getColumns();
        for (const col of columns) {
          const itemsRef = this.#getItemsRef(col.id, itemId);
          const snapshot = await get(itemsRef);
          if (snapshot.exists()) {
            columnId = col.id;
          }
        }
      }

      if (!columnId) throw new Error('Column ID could not be determined for the item');

      const itemRef = this.#getItemsRef(columnId, itemId);
      const itemSnapshot = await get(itemRef);

      if (!itemSnapshot.exists()) throw new Error('Item not found');

      const unsubscribe = onValue(itemRef, (snapshot) => {
        const data = snapshot.val();
        if (callback) callback(data);
      });

      return () => unsubscribe();
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async getItem(columnId, itemId) {
    try {
      if (!columnId) {
        const columns = await this.getColumns();
        for (const col of columns) {
          const itemsRef = this.#getItemsRef(col.id, itemId);
          const snapshot = await get(itemsRef);
          if (snapshot.exists()) {
            const itemData = snapshot.val();
            return itemData;
          }
        }
      }

      const itemRef = this.#getItemsRef(columnId, itemId);
      const snapshot = await get(itemRef);

      return snapshot.val() || null;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async duplicateItem(columnId, itemId) {
    try {
      const itemRef = this.#getItemsRef(columnId, itemId);
      const snapshot = await get(itemRef);
      const itemData = snapshot.val();

      Object.keys(itemData?.platforms || {}).forEach((key) => {
        delete itemData.platforms[key].attachments;
      });
      delete itemData.comments;
      delete itemData.logs;

      const duplicateId = await this.createItem(
        columnId,
        {
          ...itemData,
          title: `[${i18next.t('Copy')}] ${itemData.title}`,
        },
        itemData.position,
      );

      return duplicateId;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async updateItem(columnId, itemId, itemData, relativePath = null) {
    if (!columnId || !itemId) throw new Error('Column ID and Item ID are required');

    try {
      const user = getAuth().currentUser;
      const itemRef = this.#getItemsRef(columnId, itemId);
      const itemSnapshot = await get(itemRef);
      const oldItemData = itemSnapshot.val();

      let updatePayload;

      if (relativePath) {
        updatePayload = {
          [relativePath]: itemData,
        };
      } else {
        updatePayload = itemData;
      }

      await update(itemRef, updatePayload);

      if (relativePath === null && itemData.title) {
        await this.#addLogToItem(columnId, itemId, {
          type: 'TITLE_UPDATED',
          user: user?.uid,
          object: itemData.title,
          old: oldItemData.title,
        });
      }

      if (relativePath === null && itemData.priority) {
        await this.#addLogToItem(columnId, itemId, {
          type: 'PRIORITY_UPDATED',
          user: user?.uid,
          object: itemData.priority,
          old: oldItemData.priority,
        });
      }
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async deleteItem(columnId, itemId, subId = null, subPath = null) {
    if (!itemId) throw new Error('Item ID is required');

    try {
      if (!columnId) {
        const columns = await this.getColumns();
        for (const col of columns) {
          const itemsRef = this.#getItemsRef(col.id, itemId);
          const snapshot = await get(itemsRef);
          if (snapshot.exists()) {
            columnId = col.id;
          }
        }

        if (!columnId) {
          throw new Error('Column ID could not be determined for the item');
        }
      }

      const columnRef = this.#getColumnsRef(columnId);
      const columnSnapshot = await get(columnRef);
      const columnData = columnSnapshot.val();
      let itemRef = this.#getItemsRef(columnId, itemId);

      if (subId && subPath) {
        itemRef = child(itemRef, `${subPath}/${subId}`);
      }

      await set(itemRef, null);

      await update(columnRef, {
        length: (columnData.length || 1) - 1,
      });
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async transferItems(columnId, transferColumnId) {
    const columnRef = this.#getColumnsRef(columnId);
    const columnSnapshot = await get(columnRef);
    const columnData = columnSnapshot.val();
    const itemsRef = this.#getItemsRef(columnId);
    const itemsSnapshot = await get(itemsRef);
    const itemsObj = itemsSnapshot.val() || {};
    const itemsArray = Object.values(itemsObj);
    const transferColumnRef = this.#getColumnsRef(transferColumnId);
    const transferColumnSnapshot = await get(transferColumnRef);
    const transferColumnData = transferColumnSnapshot.val();

    const transferItemsRef = this.#getItemsRef(transferColumnId);
    const lastPositionQuery = query(transferItemsRef, orderByChild('position'), limitToLast(1));
    const lastPositionSnapshot = await get(lastPositionQuery);
    const lastItem = lastPositionSnapshot.exists();
    const lastPosition = lastItem ? Object.values(lastPositionSnapshot.val())[0].position : -1;

    const updates = {};

    itemsArray.forEach((item, index) => {
      updates[`${item.id}/`] = {
        ...item,
        position: lastPosition + index + 1,
      };
    });

    await update(transferItemsRef, updates);
    await update(columnRef, {
      length: (columnData.length || 0) + (transferColumnData.length || 0),
    });
  }

  async downloadItemsAsCSV(transformerFunc, headers = []) {
    try {
      const itemsRef = this.#getItemsRef();
      const snapshot = await get(itemsRef);
      const itemsObj = snapshot.val() || {};

      const columnsRef = this.#getColumnsRef();
      const columnsSnapshot = await get(columnsRef);
      const columnsObj = columnsSnapshot.val() || {};

      const usersRef = ref(getDatabase(), 'users');
      const usersSnapshot = await get(usersRef);
      const usersObj = usersSnapshot.val() || {};

      const columns = Object.values(itemsObj);
      const data = columns.reduce((acc, cur) => {
        acc.push(...Object.values(cur || {}));
        return acc;
      }, []);

      const parsedData = data
        .filter((item) => item.id)
        .map((item) => ({
          id: item.id,
          column: columnsObj?.[item.columnId]?.title || '',
          title: item.title || '',
          priority: PRIORITY_NAME?.[item.priority] || '',
          assignedTo: Array.isArray(item.assignedTo)
            ? item.assignedTo
                .map((id) =>
                  usersObj?.[id]?.name && usersObj?.[id]?.lastName
                    ? usersObj?.[id]?.name + ' ' + usersObj?.[id]?.lastName
                    : 'unknown_user',
                )
                .join(', ')
            : '',
          createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '',
          ...(typeof transformerFunc === 'function' ? transformerFunc(item) : {}),
        }));

      const csvOptions = {
        fields: [
          { label: 'ID', value: 'id' },
          { label: 'Coluna', value: 'column' },
          { label: 'Título', value: 'title' },
          { label: 'Prioridade', value: 'priority' },
          { label: 'Responsáveis', value: 'assignedTo' },
          ...headers,
          { label: 'Criado em', value: 'createdAt' },
        ],
        header: true,
      };

      const csvContent = parse(parsedData, csvOptions);

      const blob = new Blob([csvContent], { type: 'text/csv' });

      const url = URL.createObjectURL(blob);
      const accountNameRef = ref(getDatabase(), `accounts/${this.accountId}/name`);
      const accountNameSnapshot = await get(accountNameRef);
      const accountName = accountNameSnapshot.val() || 'unknown_account';
      const dateNumber = new Date().valueOf();

      const link = document.createElement('a');
      link.href = url;
      link.download = `${KANBAN_MODULE_TO_NAME[this.kanbanName] || this.kanbanName} ${accountName}.csv`;

      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async moveItem(itemId, sourceColumnId, destinationColumnId, newPosition = 0) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const firebaseToken = await user.getIdToken();

      await axios.post(
        `${process.env.REACT_APP_API_URL}/moveKanbanItem`,
        {
          accountId: this.accountId,
          kanbanName: this.kanbanName,
          itemId,
          sourceColumnId,
          destinationColumnId,
          newPosition,
          development: process.env.REACT_APP_BUILD_TYPE !== 'production',
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async addAssignedUserToItem(columnId, itemId, userId, subId = null, subPath = null) {
    try {
      const user = getAuth().currentUser;
      let itemRef = this.#getItemsRef(columnId, itemId);

      if (subId && subPath) {
        itemRef = child(itemRef, `${subPath}/${subId}`);
      }

      const snapshot = await get(itemRef);
      if (!snapshot.exists()) {
        throw new Error('Item not found');
      }

      const itemData = snapshot.val();
      let assignedTo = Array.isArray(itemData.assignedTo) ? [...itemData.assignedTo] : [];

      if (!assignedTo.includes(userId)) {
        assignedTo.push(userId);
        await update(itemRef, { assignedTo });
      }

      subId === null &&
        (await this.#addLogToItem(columnId, itemId, {
          type: 'ASSIGNEE_UPDATED',
          user: user?.uid,
          object: userId,
        }));

      if (user?.uid === userId) return;

      await this.notifyActivities(
        columnId,
        itemId,
        subPath === 'actionables' ? 'NEW_ACTIONABLE' : 'USER_ASSIGNED',
        userId,
      );
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async removeAssignedUserFromItem(columnId, itemId, userId, subId = null, subPath = null) {
    try {
      let itemRef = this.#getItemsRef(columnId, itemId);

      if (subId && subPath) {
        itemRef = child(itemRef, `${subPath}/${subId}`);
      }

      const snapshot = await get(itemRef);

      if (!snapshot.exists()) {
        throw new Error('Item not found');
      }

      const itemData = snapshot.val();
      let assignedTo = Array.isArray(itemData.assignedTo) ? [...itemData.assignedTo] : [];

      if (assignedTo.includes(userId)) {
        assignedTo = assignedTo.filter((id) => id !== userId);

        assignedTo = assignedTo.map((id) => id);

        await update(itemRef, { assignedTo });
      }
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async getTypes() {
    try {
      const typesRef = this.#getTypesRef();
      const snapshot = await get(typesRef);
      const typesObj = snapshot.val() || {};
      return typesObj;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async getTypeItems(typeName) {
    try {
      const typeItemsRef = this.#getTypesRef(typeName);
      const snapshot = await get(typeItemsRef);
      const typeItemsObj = snapshot.val() || {};
      return Object.values(typeItemsObj);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async createTypeItem(typeName, typeData) {
    try {
      const typesRef = this.#getTypesRef(typeName);
      const typeKey = push(typesRef);
      const newTypeRef = this.#getTypesRef(typeName, typeKey.key);

      await set(newTypeRef, {
        ...typeData,
        id: typeKey.key,
      });
      return { id: typeKey.key };
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async createMultipleTypeItems(typeName, typeItemsArr) {
    try {
      const typesRef = this.#getTypesRef(typeName);

      const typesData = {};
      typeItemsArr.forEach((typeItem) => {
        const newTypeRef = push(typesRef);
        typesData[newTypeRef.key] = {
          id: newTypeRef.key,
          ...typeItem,
        };
      });

      await set(typesRef, typesData);
      return true;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async updateTypeItem(typeName, typeItemId, typeData) {
    try {
      const typeItemRef = this.#getTypesRef(typeName, typeItemId);

      await update(typeItemRef, typeData);
      return typeData;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async deleteTypeItem(typeName, typeItemId) {
    try {
      const typeItemRef = this.#getTypesRef(typeName, typeItemId);

      await set(typeItemRef, null);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async createComment(columnId, itemId, comment) {
    try {
      if (!columnId) {
        const columns = await this.getColumns();
        for (const col of columns) {
          const itemsRef = this.#getItemsRef(col.id, itemId);
          const snapshot = await get(itemsRef);
          if (snapshot.exists()) {
            columnId = col.id;
          }
        }
      }

      const user = getAuth().currentUser;
      const commentsRef = this.#getItemsRef(columnId, itemId + '/comments');
      const commentKey = push(commentsRef);

      const newComment = {
        id: commentKey.key,
        createdBy: user?.uid,
        createdAt: new Date().valueOf(),
        content: comment,
      };

      await set(commentKey, newComment);

      const item = await this.getItem(columnId, itemId);
      const responsibleIds = Array.isArray(item?.assignedTo) ? item.assignedTo : [];

      for (const responsibleId of responsibleIds) {
        if (responsibleId && responsibleId !== user?.uid) continue;
        await Promise.all(
          responsibleIds
            .filter((id) => id && id !== user?.uid)
            .map((id) => this.notifyActivities(columnId, itemId, 'NEW_COMMENT', id)),
        );
      }

      return commentKey.key;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async deleteComment(columnId, itemId, commentId) {
    try {
      if (!columnId) {
        const columns = await this.getColumns();
        for (const col of columns) {
          const itemsRef = this.#getItemsRef(col.id, itemId);
          const snapshot = await get(itemsRef);
          if (snapshot.exists()) {
            columnId = col.id;
          }
        }
      }

      const commentRef = this.#getItemsRef(columnId, itemId + `/comments/${commentId}`);
      const commentSnapshot = await get(commentRef);
      const commentData = commentSnapshot.val();
      await set(commentRef, null);

      await this.#addLogToItem(columnId, itemId, {
        type: 'COMMENT_DELETED',
        user: getAuth().currentUser?.uid,
        old: commentData.content,
      });
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async uploadAttachment(columnId, itemId, path, file) {
    try {
      const attachmentRef = this.#getItemsRef(columnId, itemId + path);
      const attachmentSnapshot = await get(attachmentRef);
      const attachmentData = attachmentSnapshot.val() || [];

      if (attachmentData.length >= MAX_PLATFORM_ATTACHMENTS) {
        throw new Error('Attachment limit reached for this item');
      }

      const [fileUrl, fileId] = await this.uploadFile(
        file,
        `${this.accountId}/${this.kanbanName}/${itemId}`,
      );

      const createdAt = new Date().valueOf();
      const user = getAuth().currentUser?.uid;

      const newAttachment = {
        id: fileId,
        name: file.name,
        url: fileUrl,
        type: file.type,
        size: file.size,
        createdAt: createdAt,
        createdBy: user,
      };

      attachmentData.push(newAttachment);

      await set(attachmentRef, attachmentData);

      return newAttachment;
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async deleteAttachment(columnId, itemId, path, fileId) {
    try {
      const attachmentRef = this.#getItemsRef(columnId, itemId + path);
      const attachmentSnapshot = await get(attachmentRef);
      const attachmentData = attachmentSnapshot.val() || [];

      const attachmentIndex = attachmentData.findIndex((att) => att.id === fileId);

      if (attachmentIndex === -1) {
        throw new Error('Attachment not found');
      }

      const toBeDeletedAttach = attachmentData[attachmentIndex];
      const storage = getStorage();
      const storageAttachmentRef = storageRef(
        storage,
        `${this.accountId}/${this.kanbanName}/${itemId}/${toBeDeletedAttach.id}`,
      );
      await deleteObject(storageAttachmentRef);
      attachmentData.splice(attachmentIndex, 1);

      await set(attachmentRef, attachmentData);
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async uploadFile(file, path, attachment = true) {
    try {
      const newFileName = `${new Date().valueOf()}${file.size}${random(1000, 9999)}`;
      const storage = getStorage();
      const ref = storageRef(storage, path + `/${newFileName}`);
      const upload = await uploadBytes(ref, file, {
        contentDisposition: `${attachment ? 'attachment' : 'inline'}; filename*=UTF-8''${encodeURIComponent(file.name)}; filename="${file.name}"`,
      })
        .then((snapshot) => snapshot)
        .catch((error) => {
          console.error('Upload failed:', error);
          throw error;
        });

      const downloadURL = await getDownloadURL(upload.ref);
      return [downloadURL, newFileName];
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async getGDriveLink() {
    try {
      const db = getDatabase();

      let gDriveRef = ref(
        db,
        `accounts/${this.accountId}/gDriveFolders/${this.kanbanName}/delivery`,
      );
      let snapshot = await get(gDriveRef);

      if (!snapshot.exists() && this.kanbanName === 'paid-media') {
        gDriveRef = ref(db, `accounts/${this.accountId}/gDriveFolders/media/delivery`);
        snapshot = await get(gDriveRef);
      }

      if (!snapshot.exists()) {
        return null;
      }

      return snapshot.val();
    } catch (error) {
      throw handleFirebaseError(error);
    }
  }

  async notifyActivities(columnId, itemId, type, userId) {
    try {
      const db = getDatabase();
      const notificationsRef = ref(db, `notifications/${userId}`);

      const newNotification = {
        userId,
        columnId,
        itemId,
        type,
        read: false,
        createdAt: Date.now(),
        area: this.kanbanName,
        accountId: this.accountId,
      };

      await push(notificationsRef, newNotification);

      if (type === 'ITEM_CREATED') {
        await this.notifyInternalTeam(this.accountId, columnId, itemId, userId);
      }
    } catch (error) {
      console.error('Error in notifyActivities:', error);
    }
  }

  async notifyInternalTeam(accountId, createdBy) {
    try {
      const db = getDatabase();

      const internalTeamRef = ref(db, `accounts/${accountId}/internalTeam`);
      const snapshot = await get(internalTeamRef);

      if (!snapshot.exists()) return;

      const teamData = snapshot.val();
      const teamIds = Array.isArray(teamData) ? teamData : Object.values(teamData);
      const usersToNotify = teamIds.filter((userId) => userId && userId !== createdBy);
      console.log('Users to notify:', usersToNotify);
    } catch (error) {
      console.error(error);
    }
  }
}
